import produce from "immer";
import { defaultDecodedJWTToken } from "./AuthProvider.state";
import { IAuthContextState, IWhatsappService, IUserSettings } from "./AuthProvider.types";
import {
  decodeJWTToken,
  saveUserStateInLocalStorage,
} from "./AuthProvider.utils";
import { WHATSAPP_SERVICE_CONFIG } from "../../constants/whatsapp.constatnts";
import { isArray } from "lodash";
import { getCountryByName } from "../../helpers/countryTimezone.helpers";
import { ICompanyOutlet } from "../../redux/companyOutlet/companyOutlet.types";

export const createUserStatefromResponse = (
  authState: IAuthContextState,
  data: any,
): IAuthContextState => {
  const jsonTokenData = decodeJWTToken(data.token);
  const finalJsonTokenData = jsonTokenData
    ? jsonTokenData
    : defaultDecodedJWTToken;

  const newUserState: IAuthContextState = {
    ...authState,
    user: {
      first_name: data.user.first_name,
      last_name: data.user.last_name || "",
      full_name: data.user.full_name,
      name: data.user.full_name,
      email: data.user.email,
      role: data.user.role_value as "ADMIN",
      userId: data.user.user_fact_id,
      userUuid: data.user.user_uuid,
      role_value: data.user.role_value,

      affilated_company_outlet_uuids: Array.isArray(data.user.affilated_company_outlet_uuids)
        ? data.user.affilated_company_outlet_uuids.filter(
            (outlet: any): outlet is ICompanyOutlet => !!outlet?.company_outlet_uuid
          )
        : [],
      shared_emails:
        "shared_email" in data.user ? (data.user.shared_email as any) : [],
      mobile: data.user?.mobile || "",
    },
    app_info: {
      company_name: finalJsonTokenData.COMPANY_NAME,
      company_logo: finalJsonTokenData.LOGO,
    },
    isLogin: data.isOTPPreview === false ? true : false,
    token: data.token,
    force_logout: data.force_logout,
    sessionCount: data.sessionCount,
    twoFactorData: {
      userFactId: data.user.user_fact_id,
    },
    company_outlet_timezone: finalJsonTokenData.company_outlet_timezone,
    country: getCountryByName(finalJsonTokenData.default_country),
    subscribed_plan:
      "subscribed_plan" in data.user
        ? (data.user.subscribed_plan as any)
        : null,
    gst_percentage: finalJsonTokenData.gst_percentage,
    userSettings: "user_settings" in data ? data.user_settings : [],
    isSimDialingEnabled: "user_settings" in data ? hasSimDialingEnabled(data.user_settings) : false
  };

  return newUserState;
};

export const updateLocalStoreFromUserSync = (
  loacalAuthState: IAuthContextState,
  data: any,
): IAuthContextState => {
  let newAuthState: IAuthContextState | null = { ...loacalAuthState };
  // Refresh mobile from the authoritative user profile (/user/get-user).
  if (data && "mobile" in data) {
    newAuthState.user = {
      ...newAuthState.user,
      mobile: data.mobile || "",
    };
  }
  if ("module_security" in data) {
    newAuthState.security = data.module_security ?? [];
  }
  if ("subscribed_plan" in data) {
    newAuthState.subscribed_plan = data.subscribed_plan ?? null;
  }
  if ("user_settings" in data) {
    newAuthState.userSettings = data.user_settings
    newAuthState.isSimDialingEnabled = hasSimDialingEnabled(data.user_settings)
  }
  saveUserStateInLocalStorage(newAuthState);
  return newAuthState;
};

export const updateLocalStoreFromExternalUserSync = (
  loacalAuthState: IAuthContextState,
  data: any,
): IAuthContextState => {
  return produce({ ...loacalAuthState }, (draftState) => {
    if (draftState.external_auth) {
      if (data && "mobile" in data) {
        draftState.external_auth.user = {
          ...draftState.external_auth.user,
          mobile: data.mobile || "",
        }
      }
      if ("module_security" in data) {
        draftState.external_auth.security = data.module_security ?? []
      }
      if ("subscribed_plan" in data) {
        draftState.external_auth.subscribed_plan = data.subscribed_plan ?? []
      }
      if ("user_settings" in data) {
        draftState.external_auth.userSettings = data.user_settings
        draftState.external_auth.isSimDialingEnabled = hasSimDialingEnabled(data.user_settings)
      }
    }
  })
};


export const convertToExternalUser = (data: any): IAuthContextState => {
  const jsonTokenData = decodeJWTToken(data.token);
  const finalJsonTokenData = jsonTokenData
    ? jsonTokenData
    : defaultDecodedJWTToken;

  const newUserState: IAuthContextState = {
    user: {
      first_name: data.user.first_name,
      last_name: data.user.last_name || "",
      full_name: data.user.full_name,
      name: data.user.full_name,
      email: data.user.email,
      role: data.user.role_value as "ADMIN",
      userId: data.user.user_fact_id,
      userUuid: data.user.user_uuid,
      role_value: data.user.role_value,

      affilated_company_outlet_uuids: Array.isArray(data.user.affilated_company_outlet_uuids)
        ? data.user.affilated_company_outlet_uuids.filter(
            (outlet: any): outlet is ICompanyOutlet => !!outlet?.company_outlet_uuid
          )
        : [],
      shared_emails:
        "shared_email" in data.user ? (data.user.shared_email as any) : [],
      mobile: data.user?.mobile || "",
    },
    app_info: {
      company_name: finalJsonTokenData.COMPANY_NAME,
      company_logo: finalJsonTokenData.LOGO,
    },
    isLogin: true,
    token: data.token,
    force_logout: data.force_logout,
    sessionCount: data.sessionCount,
    twoFactorData: {
      userFactId: data.user.user_fact_id,
    },
    company_outlet_timezone: finalJsonTokenData.company_outlet_timezone,
    country: getCountryByName(finalJsonTokenData.default_country),
    subscribed_plan:
      "subscribed_plan" in data.user
        ? (data.user.subscribed_plan as any)
        : null,
    gst_percentage: finalJsonTokenData.gst_percentage,
    external_auth: null,
    security: data.module_security ?? [],
    date: new Date(),
    userSettings: "user_settings" in data ? data.user_settings : null,
    isSimDialingEnabled: "user_settings" in data ? hasSimDialingEnabled(data.user_settings) : false
  }

  return newUserState;
};


const getWhatsappServiceProvider = (user_settings: any) => {
  const waSetting = user_settings.find((item: any) => item.env_key === "WHATSAPP_PLATFORM_SERVICE")
  return waSetting ? waSetting?.setting_value : null
}

const getWhatsappServiceNumber = (user_settings: any) => {
  const waSetting = user_settings.find((item: any) => item.env_key === "BAILEY_WHATSAPP_NUMBER")
  return waSetting ? waSetting?.setting_value : null
}


export const getWhatsappServiceStatus = (user_settings: IUserSettings[]): IWhatsappService => {
  const waPlatforms = user_settings.find((item) => item.env_key === WHATSAPP_SERVICE_CONFIG.CURRENT_WHATSAPP_PLATFORMS);
  const validPlatforms = waPlatforms ? JSON.parse(waPlatforms.setting_value) : []
  if (isArray(validPlatforms) && validPlatforms.length > 0) {
    const waPlatforms = user_settings.find((item) => item.env_key === WHATSAPP_SERVICE_CONFIG.CURRENT_SELECTED_WHATSAPP_PLATFORM);
    const waNumber = user_settings.find((item) => item.env_key === WHATSAPP_SERVICE_CONFIG.BAILEY_WHATSAPP_NUMBER);
    return {
      isMultiple: validPlatforms.length > 1 ? true : false,
      serviceProviders: validPlatforms as string[],
      selectedService: waPlatforms ? waPlatforms.setting_value : validPlatforms[0],
      whatsappNumber: waNumber ? waNumber.setting_value : ""
    }
  }
  return {
    isMultiple: false,
    serviceProviders: [],
    selectedService: null,
    whatsappNumber: ""
  }
}

export const isBaileyActivated = (service: IWhatsappService): boolean => {
  return service.serviceProviders.includes("BAILEY")
}
export const isBaileyProvider = (service: IWhatsappService): boolean => {
  return service.selectedService === "BAILEY"
}


export const isMetaProvider = (service: IWhatsappService): boolean => {
  return service.selectedService === "META"
}


export const isWhatsappActivated = (service: IWhatsappService): boolean => {
  return service.serviceProviders.includes(service.selectedService as "META")
}


export const hasSimDialingEnabled = (settings: IUserSettings[]): boolean => {
  return settings.some(
    (setting) =>
      setting.env_key === "SIM_BASED_DIALING" &&
      setting.setting_value.toLowerCase() === "true"
  );
};